/**
 * useVehiclesModule - Fonte única do módulo de Veículos
 *
 * Expõe a flag `vehicles_enabled` do tenant, as configurações de consulta por
 * placa/multas e as categorias de veículo (usadas pelo tipo "Outros"), além
 * das mutations de configuração.
 * Todo componente relacionado a veículos deve consultar `isEnabled` daqui.
 *
 * @version 1.3.0
 * RELEASE: 2026-08-24 v1.3.0 - Nova configuração scope_to_customer (restringe a seleção de
 *            veículos aos do cliente/contato do registro)
 * RELEASE: 2026-08-08 v1.1.0 - Novas configurações: consulta por placa, multas e UF padrão
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */


import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useVehiclesModule');

/** Categoria de veículo do tenant (tipo OTHER) */
export interface VehicleCategory {
  id: string;
  name: string;
  is_active: boolean;
}

/** Payload da RPC get_vehicles_settings */
interface VehiclesSettings {
  vehicles_enabled: boolean;
  /** v1.1.0 — Consulta automática de dados do veículo pela placa */
  plate_lookup_enabled: boolean;
  /** v1.1.0 — Consulta de multas em tempo real */
  fines_enabled: boolean;
  /** v1.1.0 — UF padrão usada na consulta de multas */
  fines_uf: string | null;
  /** v1.2.0 — Validade (dias) do cache de consulta por placa */
  plate_cache_ttl_days: number;
  /** v1.3.0 — Restringe os seletores de veículo aos veículos do cliente/contato */
  scope_to_customer: boolean;
  categories: VehicleCategory[];
}

/**
 * Hook de configurações do módulo de veículos.
 * Usado tanto pela aba de Configurações quanto pelos gates de UI.
 */
export function useVehiclesModule() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['vehicles-settings', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<VehiclesSettings> => {
      const { data, error } = await (supabase.rpc as any)('get_vehicles_settings', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar configurações de veículos', { error: error.message });
        throw error;
      }
      const payload = (data || {}) as Partial<VehiclesSettings>;
      return {
        vehicles_enabled: payload.vehicles_enabled ?? false,
        plate_lookup_enabled: payload.plate_lookup_enabled ?? false,
        fines_enabled: payload.fines_enabled ?? false,
        fines_uf: payload.fines_uf ?? null,
        plate_cache_ttl_days: payload.plate_cache_ttl_days ?? 90,
        scope_to_customer: payload.scope_to_customer ?? false,
        categories: payload.categories ?? [],
      };
    },
  });

  /** Invalida o cache das configurações do módulo */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicles-settings', tenantId] });
  };

  /** Liga/desliga o módulo de veículos (somente admin — validado na RPC) */
  const toggleModule = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await (supabase.rpc as any)('update_vehicles_settings', {
        p_vehicles_enabled: enabled,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return enabled;
    },
    onSuccess: (enabled) => {
      invalidate();
      toast.success(enabled ? 'Módulo de veículos habilitado' : 'Módulo de veículos desabilitado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /**
   * v1.1.0 — Atualiza as configurações de consulta por placa / multas.
   * Campos não informados preservam o valor atual (alteração cirúrgica na RPC).
   */
  const updateLookupSettings = useMutation({
    mutationFn: async (input: {
      plateLookupEnabled?: boolean;
      finesEnabled?: boolean;
      finesUf?: string | null;
      /** v1.2.0 — Validade (dias) do cache de consulta por placa */
      plateCacheTtlDays?: number | null;
      /** v1.3.0 — Restringe a seleção de veículos aos do cliente/contato */
      scopeToCustomer?: boolean | null;
    }) => {
      const { error } = await (supabase.rpc as any)('update_vehicles_settings', {
        p_vehicles_enabled: query.data?.vehicles_enabled ?? false,
        p_tenant_hint: tenantId ?? null,
        p_plate_lookup_enabled: input.plateLookupEnabled ?? null,
        p_fines_enabled: input.finesEnabled ?? null,
        p_fines_uf: input.finesUf ?? null,
        p_plate_cache_ttl_days: input.plateCacheTtlDays ?? null,
        p_scope_to_customer: input.scopeToCustomer ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Configurações atualizadas');
    },
    onError: (error: Error) => toast.error(error.message),
  });


  /** Cria ou edita uma categoria de veículo */
  const upsertCategory = useMutation({
    mutationFn: async (input: { id?: string | null; name: string; isActive?: boolean }) => {
      const { data, error } = await (supabase.rpc as any)('upsert_vehicle_category', {
        p_name: input.name,
        p_category_id: input.id ?? null,
        p_is_active: input.isActive ?? true,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Categoria salva');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /** Exclui uma categoria sem veículos vinculados */
  const deleteCategory = useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await (supabase.rpc as any)('delete_vehicle_category', {
        p_category_id: categoryId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Categoria excluída');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return {
    isEnabled: query.data?.vehicles_enabled ?? false,
    // v1.1.0 — flags da integração de placa/multas
    plateLookupEnabled: query.data?.plate_lookup_enabled ?? false,
    finesEnabled: query.data?.fines_enabled ?? false,
    finesUf: query.data?.fines_uf ?? null,
    // v1.2.0 — validade (dias) do cache de consulta por placa
    plateCacheTtlDays: query.data?.plate_cache_ttl_days ?? 90,
    // v1.3.0 — restringe a seleção de veículos aos do cliente/contato
    scopeToCustomer: query.data?.scope_to_customer ?? false,
    categories: query.data?.categories ?? [],
    activeCategories: (query.data?.categories ?? []).filter((c) => c.is_active),
    isLoading: query.isLoading,
    toggleModule,
    updateLookupSettings,
    upsertCategory,
    deleteCategory,
  };

}

/** Atalho somente-leitura para gates de UI */
export function useVehiclesModuleEnabled(): boolean {
  return useVehiclesModule().isEnabled;
}
